import { useState, useEffect } from "react";
import { StepInstanceResponse } from "../../../../../../model/workflows/step-instance";
import { FieldType } from "../../../../../../model/service-request/attribute";
import Select, { SelectItem } from "../../../../../../common/components/new/select/Select";
import styles from "./StepInstanceForm.module.scss";
import DynamicForm from "../../../../../dynamic-forms/components/DynamicForm";
import { DynamicFieldItem } from "../../../../../../model/dynamic-forms";
import { SectionDefinition } from "../../../../../../model/service-request/section-definition";
import { ServiceRequestInstance } from "../../../../../../model/service-request/service-request-instance";
import { ServiceRequestType } from "../../../../../../model/service-request/service-request";

interface StepInstanceFormProps {
	serviceRequestInstance: ServiceRequestInstance | undefined;
	serviceRequestType: ServiceRequestType | undefined;
	onFormValuesChange?: (fieldValues: DynamicFieldItem[]) => void;
	isReadOnly?: boolean;
	initialFormValues?: DynamicFieldItem[];
}

const StepInstanceForm = ({ serviceRequestInstance, serviceRequestType, onFormValuesChange, isReadOnly = false, initialFormValues }: StepInstanceFormProps) => {
	// Map attributeValues from serviceRequestInstance to DynamicFieldItem[]
	const mapAttributeValuesToDynamicFieldItems = (): DynamicFieldItem[] => {
		if (!serviceRequestInstance || !serviceRequestType) {
			return [];
		}

		const fieldItems: DynamicFieldItem[] = [];

		serviceRequestInstance.sectionInstances.forEach(sectionInstance => {
			if (sectionInstance.isDeleted) return;

			sectionInstance.attributeValues.forEach(attributeValue => {
				if (attributeValue.isDeleted) return;

				const { attributeDefinition } = attributeValue;
				if (!attributeDefinition) return;

				let fieldValue: any;
				let fieldPath: string;
				let fileFileName: string | undefined;

				// Determine field value and path based on field type
				if (attributeDefinition.fieldType === FieldType.File || attributeDefinition.fieldType === FieldType.Image) {
					// For file fields, use urlValue and extract file name
					if (attributeValue.urlValue) {
						fieldValue = attributeValue.urlValue; // Store URL or file name
						fieldPath = `${attributeDefinition.id}.file`;
						// Extract file name from URL
						if (attributeValue.urlValue.includes("/") || attributeValue.urlValue.includes("\\")) {
							fileFileName = attributeValue.urlValue.split("/").pop() || attributeValue.urlValue.split("\\").pop() || attributeValue.urlValue;
						} else {
							fileFileName = attributeValue.urlValue;
						}
					} else {
						return; // Skip if no file value
					}
				} else if (attributeValue.stringValue !== null && attributeValue.stringValue !== undefined && attributeValue.stringValue !== "") {
					fieldValue = attributeValue.stringValue;
					fieldPath = `${attributeDefinition.id}.stringValue`;
				} else if (attributeValue.doubleValue !== null && attributeValue.doubleValue !== undefined) {
					fieldValue = attributeValue.doubleValue;
					fieldPath = `${attributeDefinition.id}.doubleValue`;
				} else if (attributeValue.boolValue !== null && attributeValue.boolValue !== undefined) {
					fieldValue = attributeValue.boolValue;
					fieldPath = `${attributeDefinition.id}.boolValue`;
				} else if (attributeValue.dateValue) {
					fieldValue = new Date(attributeValue.dateValue);
					fieldPath = `${attributeDefinition.id}.dateValue`;
				} else if (attributeValue.selectedOptionIds && attributeValue.selectedOptionIds.length > 0) {
					fieldValue = attributeValue.selectedOptionIds[0];
					fieldPath = `${attributeDefinition.id}.selectedOptionIds[0]`;
				} else {
					return; // Skip if no value
				}

				const fieldLabel = attributeDefinition.placeholderText || attributeDefinition.label;

				fieldItems.push({
					fieldId: attributeDefinition.id,
					fieldValue,
					fieldType: attributeDefinition.fieldType!,
					fieldLabel,
					fieldPath,
					fileFileName,
				});
			});
		});

		return fieldItems;
	};

	const getInitialValues = (): DynamicFieldItem[] => {
		// Use provided initialFormValues if available (for state persistence)
		if (initialFormValues && initialFormValues.length > 0) {
			return initialFormValues;
		}
		// Otherwise, map from serviceRequestInstance
		return mapAttributeValuesToDynamicFieldItems();
	};

	const [currentFieldValues, setCurrentFieldValues] = useState<DynamicFieldItem[]>(getInitialValues());

	// Initialize with values from serviceRequestInstance only if initialFormValues is not provided
	useEffect(() => {
		// Only update from serviceRequestInstance if we don't have persisted values
		if (!initialFormValues || initialFormValues.length === 0) {
			const updatedValues = mapAttributeValuesToDynamicFieldItems();
			if (updatedValues.length > 0) {
				setCurrentFieldValues(updatedValues);
				if (onFormValuesChange) {
					onFormValuesChange(updatedValues);
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [serviceRequestInstance, serviceRequestType]);

	// Update state when initialFormValues changes (when switching tabs with persisted state)
	useEffect(() => {
		if (initialFormValues && initialFormValues.length > 0) {
			setCurrentFieldValues(initialFormValues);
		}
	}, [initialFormValues]);

	// Render dynamic form based on additionalServiceRequest
	const renderServiceRequestFields = () => {
		if (!serviceRequestType) {
			return null;
		}

		const { sectionDefinitions } = serviceRequestType;
		const preselectedFormValues = currentFieldValues;

		return (
			<div className={styles.additionalFieldsContainer}>
				{sectionDefinitions
					.filter(section => !section.isDeleted)
					.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
					.map(section => (
						<div key={section.id || section.name} className={styles.section}>
							{section.name && <h3 className={styles.sectionTitle}>{section.name}</h3>}
							<DynamicForm
								sectionDefinition={section}
								preselectedFormValues={preselectedFormValues.filter(item => section.attributeDefinitions.some(attr => attr.id === item.fieldId))}
								isSubmitted={false}
								isReadOnly={isReadOnly}
								formStateChanged={(fieldValues, isFormValid) => {
									// Don't update if read-only
									if (isReadOnly) {
										return;
									}
									// Merge field values from all sections
									const updatedValues = [...currentFieldValues];
									fieldValues.forEach(fv => {
										const index = updatedValues.findIndex(v => v.fieldId === fv.fieldId);
										if (index !== -1) {
											updatedValues[index] = fv;
										} else {
											updatedValues.push(fv);
										}
									});
									setCurrentFieldValues(updatedValues);
									if (onFormValuesChange) {
										onFormValuesChange(updatedValues);
									}
								}}
							/>
						</div>
					))}
			</div>
		);
	};

	return (
		<div className={styles.formContainer}>
			{/* Dynamic fields from additionalServiceRequest */}
			{renderServiceRequestFields()}
		</div>
	);
};

export default StepInstanceForm;
